import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeIcon from "@mui/icons-material/Home";
import { convertISTToTimezone, formatDateToISO, calculateDuration, } from "./utils/date";
import { getCurrencySymbol } from "../../constants";
import { useTranslation } from "../../hooks";
import { AppointmentSummaryCard } from "./AppointmentSummaryCard";
import { bookedChargeAmount } from "../../store/slices/bookedAppointment";
const DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
export function BottomBar({ state, dispatch, onBook, onContinueFromForm, onContinue, onPayAtClinic, onConfirmAppointment, onGoBack, onBookAnother, onBackToHome, isMobile = false, hideBackButton = false, hidePrimaryAction = false, systemConfig, onPayOnline, showPayOnline = false, paymentChoice, primaryActionBlocked = false, pricePending = false, }) {
    const { t } = useTranslation("appointment");
    const { t: tCommon } = useTranslation("common");
    const { selectedDoctor, selectedSlot, selectedDate, selectedTimezone, consultationMode, selectedPack, selectedActiveSessionPack, planQuotes, quote, currentStep, bookingLoading, } = state;
    const canBook = !!(selectedDoctor && selectedSlot);
    const currencySymbol = quote && quote.kind !== "ACTIVE_PACK" ? getCurrencySymbol(quote.currency) : "";
    const showFeeInBottomBar = systemConfig?.showFeeInBottomBar ?? true;
    const scheduleCTALabel = systemConfig
        ? t(systemConfig.scheduleCTA.key, systemConfig.scheduleCTA.fallback)
        : t("bookButton", "Book Appointment");
    const summaryCTALabel = systemConfig
        ? t(systemConfig.summaryCTA.key, systemConfig.summaryCTA.fallback)
        : t("payAtClinic", "Pay at Clinic");
    const timeDisplay = useMemo(() => {
        if (!selectedSlot)
            return null;
        const dateStr = formatDateToISO(selectedDate);
        const startStr = selectedSlot.start.includes("T")
            ? selectedSlot.start.split("T")[1].substring(0, 5)
            : selectedSlot.start.substring(0, 5);
        const displayStart = convertISTToTimezone(startStr, dateStr, selectedTimezone);
        const month = MONTHS[selectedDate.getMonth()];
        const date = selectedDate.getDate();
        const year = selectedDate.getFullYear();
        const mode = consultationMode === "ONLINE" ? t("online") : t("inPerson");
        if (systemConfig?.slotTimeFormat === "range") {
            const endStr = selectedSlot.end.includes("T")
                ? selectedSlot.end.split("T")[1].substring(0, 5)
                : selectedSlot.end.substring(0, 5);
            const displayEnd = convertISTToTimezone(endStr, dateStr, selectedTimezone);
            return {
                dateStr: `${date} ${month} ${year} | ${displayStart} – ${displayEnd}`,
                mode,
                duration: null,
            };
        }
        const duration = calculateDuration(selectedSlot.start, selectedSlot.end, 15);
        return {
            dateStr: `${date} ${month} ${year} | ${displayStart}`,
            mode,
            duration: t("consultationDuration", undefined, { count: duration }),
        };
    }, [
        selectedSlot,
        selectedDate,
        selectedTimezone,
        consultationMode,
        systemConfig,
        t,
    ]);
    const totalDue = quote ? bookedChargeAmount(quote) : null;
    const listAmount = quote?.kind === "PACK_PURCHASE" && selectedPack
        ? (planQuotes.packs[String(selectedPack.id)]?.listAmount ?? null)
        : null;
    const originalPrice = quote?.kind === "PACK_PURCHASE" && listAmount !== null && listAmount > quote.amount
        ? listAmount
        : null;
    if (!selectedDoctor)
        return null;
    const isFreeBooking = quote?.kind === "ACTIVE_PACK" || totalDue === 0;
    const renderCTAContent = () => {
        if (currentStep === "appointment-form") {
            return (_jsx(Button, { variant: "contained", onClick: onContinueFromForm, fullWidth: true, disabled: bookingLoading, endIcon: bookingLoading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 16 } })), sx: { minHeight: 44 }, children: "Continue" }));
        }
        if (currentStep === "patient-selection") {
            return (_jsx(Button, { variant: "contained", onClick: onContinue, fullWidth: true, disabled: bookingLoading, endIcon: bookingLoading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 16 } })), sx: { minHeight: 44 }, children: bookingLoading ? (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [_jsx(CircularProgress, { size: 14, color: "inherit" }), _jsx("span", { children: tCommon("processing") })] })) : (t("continue")) }));
        }
        if (currentStep === "summary") {
            if (isFreeBooking) {
                return (_jsx(Button, { variant: "contained", onClick: onConfirmAppointment, fullWidth: true, disabled: bookingLoading || primaryActionBlocked, endIcon: bookingLoading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 16 } })), sx: { minHeight: 44 }, children: bookingLoading ? (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [_jsx(CircularProgress, { size: 14, color: "inherit" }), _jsx("span", { children: tCommon("processing") })] })) : (t("confirmAppointment")) }));
            }
            if (paymentChoice) {
                const isOnline = paymentChoice === "ONLINE" && !!onPayOnline;
                return (_jsx(Button, { variant: "contained", onClick: isOnline ? onPayOnline : onPayAtClinic, fullWidth: true, disabled: bookingLoading || primaryActionBlocked, endIcon: bookingLoading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 16 } })), sx: { minHeight: 44 }, children: bookingLoading ? (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [_jsx(CircularProgress, { size: 14, color: "inherit" }), _jsx("span", { children: tCommon("processing") })] })) : isOnline ? (t("payOnline", "Pay Online")) : paymentChoice === "MANUAL" ? (t("confirmBooking", "Confirm booking")) : (summaryCTALabel) }));
            }
            if (showPayOnline && onPayOnline) {
                const payOnlineBtn = (_jsx(Button, { variant: "contained", onClick: onPayOnline, fullWidth: true, disabled: bookingLoading || primaryActionBlocked, endIcon: bookingLoading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 16 } })), sx: { minHeight: 44 }, children: bookingLoading ? (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [_jsx(CircularProgress, { size: 14, color: "inherit" }), _jsx("span", { children: tCommon("processing") })] })) : (t("payOnline", "Pay Online")) }));
                if (consultationMode === "ONLINE") {
                    return payOnlineBtn;
                }
                return (_jsxs(Stack, { direction: "column", spacing: 0.75, children: [payOnlineBtn, _jsx(Button, { variant: "outlined", onClick: onPayAtClinic, fullWidth: true, disabled: bookingLoading || primaryActionBlocked, sx: { minHeight: 44 }, children: summaryCTALabel })] }));
            }
            return (_jsx(Button, { variant: "contained", onClick: onPayAtClinic, fullWidth: true, disabled: bookingLoading || primaryActionBlocked, endIcon: bookingLoading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 16 } })), sx: { minHeight: 44 }, children: bookingLoading ? (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [_jsx(CircularProgress, { size: 14, color: "inherit" }), _jsx("span", { children: tCommon("processing") })] })) : (summaryCTALabel) }));
        }
        return null;
    };
    const renderButtons = () => {
        if (currentStep !== "success")
            return null;
        return (_jsxs(Stack, { direction: isMobile ? "column" : "row", spacing: isMobile ? 1 : 1.5, sx: { width: isMobile ? "100%" : "auto" }, children: [_jsx(Button, { variant: "outlined", component: onBackToHome ? "button" : "a", href: onBackToHome ? undefined : "/", onClick: onBackToHome, startIcon: _jsx(HomeIcon, { sx: { fontSize: 16 } }), sx: {
                        whiteSpace: "nowrap",
                        ...(isMobile ? { width: "100%", minHeight: 44 } : {}),
                    }, type: onBackToHome ? "button" : undefined, children: t("backToHome", "Back to Home") }), _jsx(Button, { variant: "contained", onClick: onBookAnother, endIcon: _jsx(ArrowForwardIcon, { sx: { fontSize: 16 } }), sx: {
                        whiteSpace: "nowrap",
                        ...(isMobile ? { width: "100%", minHeight: 48 } : {}),
                    }, children: t("bookAnotherAppointment", "Book Another Appointment") })] }));
    };
    return (_jsxs(Box, { sx: {
            position: "sticky",
            bottom: 0,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
            zIndex: 10,
            boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
        }, children: [_jsx(Box, { sx: { px: isMobile ? 2 : 2.5, pt: 1.5, pb: 1.75 }, children: currentStep === "success" ? (_jsx(Stack, { direction: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", spacing: isMobile ? 1 : 1.5, sx: { justifyContent: isMobile ? "stretch" : "flex-end" }, children: renderButtons() })) : (_jsxs(_Fragment, { children: [!hideBackButton &&
                            !(isMobile && currentStep === "phone") &&
                            (currentStep === "phone" ||
                                currentStep === "otp" ||
                                currentStep === "appointment-form" ||
                                currentStep === "patient-selection" ||
                                currentStep === "summary") && (_jsx(Button, { variant: "text", size: "small", onClick: onGoBack, disabled: bookingLoading, startIcon: _jsx(ChevronLeftIcon, { sx: { fontSize: 16 } }), color: "secondary", sx: { mb: 0.75, px: 0.5, display: "flex" }, children: tCommon("back") })), (currentStep === "schedule" || timeDisplay) && (_jsx(AppointmentSummaryCard, { isMobile: isMobile, consultationMode: consultationMode, timeDisplay: timeDisplay, totalDue: totalDue, currencySymbol: currencySymbol, originalPrice: originalPrice, isFreeBooking: isFreeBooking, selectedPackLabel: selectedPack?.packageName ?? null, selectedActiveSessionPack: selectedActiveSessionPack, showFee: showFeeInBottomBar, feePending: pricePending, label: systemConfig
                                ? t(systemConfig.selectedItem.key, systemConfig.selectedItem.fallback)
                                : t("selectedAppointment", "Selected Appointment"), showModeChips: currentStep !== "schedule", t: t })), selectedActiveSessionPack && timeDisplay && (_jsx(Box, { sx: { mt: 1, mb: 1 }, children: _jsx(Typography, { variant: "caption", sx: { color: "success.main", fontWeight: 600 }, children: t("sessionPackAppliedIndicator") }) })), currentStep === "schedule" && !hidePrimaryAction && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                                        height: "1px",
                                        bgcolor: "divider",
                                        mt: isMobile ? 1.25 : 1.75,
                                        mb: isMobile ? 1.25 : 1.75,
                                    }, "aria-hidden": true }), _jsx(Box, { sx: {
                                        display: "flex",
                                        justifyContent: "center",
                                    }, children: _jsx(Button, { variant: "contained", onClick: onBook, disabled: !canBook || bookingLoading || primaryActionBlocked, endIcon: bookingLoading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 16 } })), fullWidth: isMobile, sx: {
                                            minHeight: isMobile ? 48 : 52,
                                            minWidth: isMobile ? undefined : 320,
                                            px: isMobile ? undefined : 6,
                                            fontSize: isMobile ? 15 : 17,
                                            fontWeight: 600,
                                            borderRadius: 1.5,
                                        }, children: bookingLoading ? (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [_jsx(CircularProgress, { size: 14, color: "inherit" }), _jsx("span", { children: tCommon("processing") })] })) : (scheduleCTALabel) }) })] })), (currentStep === "appointment-form" ||
                            currentStep === "patient-selection" ||
                            currentStep === "summary") &&
                            !hidePrimaryAction && (_jsx(Box, { sx: { mt: 1.5 }, children: renderCTAContent() }))] })) }), _jsxs(Box, { sx: {
                    py: { xs: 0.75, sm: 1 },
                    px: { xs: 2, sm: 2.5 },
                    borderTop: 1,
                    borderColor: "divider",
                    bgcolor: "neutral.surfaceHover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    flexShrink: 0,
                }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", children: "Powered by" }), _jsx("a", { href: "https://www.medos.one", target: "_blank", rel: "noopener noreferrer", style: { display: "flex", alignItems: "center" }, children: _jsx("img", { src: "https://prod-medos-media-public-bucket.s3.ap-south-1.amazonaws.com/assets/logo.png", alt: "MedOS", style: { height: 18, objectFit: "contain" } }) })] })] }));
}
